// ✅ src/components/ProfileDetailsScreen.js (FULL)
// FIX: desktop web "About Me" looked too small
// ✅ Wider container on web
// ✅ Middle column gets more width on desktop
// ✅ About box taller + better text wrapping on desktop

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../lib/firebase";
import { doc, getDoc } from "firebase/firestore";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}

function titleCase(v) {
  const t = safe(v);
  if (!t) return "";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function msFromBirthDateAny(bd) {
  try {
    if (!bd) return null;

    if (typeof bd === "number") return bd;

    if (typeof bd === "string") {
      const d = new Date(bd);
      const ms = d.getTime();
      return Number.isNaN(ms) ? null : ms;
    }

    if (bd instanceof Date) return bd.getTime();

    if (bd?.toMillis) return bd.toMillis();
    if (bd?.toDate) return bd.toDate().getTime();

    return null;
  } catch {
    return null;
  }
}

function calcAgeFromBirthDateAny(bd) {
  const ms = msFromBirthDateAny(bd);
  if (!ms) return null;
  const dob = new Date(ms);
  const now = new Date();

  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;

  if (age < 0 || age > 120) return null;
  return age;
}

function initials(name) {
  const t = safe(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}

function uniq(arr) {
  return Array.from(new Set((arr || []).map((x) => safe(x)).filter(Boolean)));
}

function prettyEnum(v) {
  const t = safe(v);
  if (!t) return "";
  return titleCase(t.replaceAll("_", " "));
}

function displayJob(p) {
  const job = safe(p?.job);
  const jobOther = safe(p?.jobOther);
  if (!job) return "";
  if (job === "other") return jobOther || "Other";
  return prettyEnum(job);
}

function valueOrDash(v) {
  const t = safe(v);
  return t ? t : "—";
}

/* ---------------- component ---------------- */
export default function ProfileDetailsScreen() {
  const nav = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 980);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const styles = useMemo(() => {
    const pageBg = "linear-gradient(180deg, #eef2f7 0%, #e9eff6 100%)";
    const cardBorder = "1px solid #dbe4ef";
    const shadow = "0 12px 28px rgba(18, 38, 63, 0.10)";
    const blue = "#2f5d96";
    const blue2 = "#2a5287";

    return {
      page: { minHeight: "100vh", background: pageBg, color: "#1f2a37" },

      /* ---------- top nav ---------- */
      topBar: {
        position: "sticky",
        top: 0,
        zIndex: 30,
        background: `linear-gradient(180deg, ${blue} 0%, ${blue2} 100%)`,
        boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
      },
      topBarInner: {
        maxWidth: isNarrow ? 1140 : 1320, // ✅ wider on desktop
        margin: "0 auto",
        padding: isNarrow ? "10px 12px" : "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      },
      brand: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        color: "#fff",
        fontWeight: 950,
        letterSpacing: -0.2,
        whiteSpace: "nowrap",
        cursor: "pointer",
      },
      brandDot: {
        width: 12,
        height: 12,
        borderRadius: 99,
        background: "linear-gradient(135deg, #ffffff, rgba(255,255,255,0.55))",
        boxShadow: "0 10px 18px rgba(0,0,0,0.18)",
      },
      rightTop: { display: "flex", alignItems: "center", gap: 10 },

      topAvatar: {
        width: 34,
        height: 34,
        borderRadius: 99,
        border: "2px solid rgba(255,255,255,0.40)",
        objectFit: "cover",
        background: "rgba(255,255,255,0.20)",
      },
      topAvatarFallback: {
        width: 34,
        height: 34,
        borderRadius: 99,
        border: "2px solid rgba(255,255,255,0.40)",
        display: "grid",
        placeItems: "center",
        fontWeight: 950,
        color: "#fff",
        background: "rgba(255,255,255,0.18)",
      },

      topBackBtn: {
        border: "1px solid rgba(255,255,255,0.28)",
        background: "rgba(255,255,255,0.12)",
        color: "#fff",
        fontWeight: 900,
        cursor: "pointer",
        padding: "10px 12px",
        borderRadius: 10,
      },

      /* ---------- body grid ---------- */
      container: {
        maxWidth: isNarrow ? 1140 : 1320, // ✅ wider on desktop
        margin: "0 auto",
        padding: isNarrow ? 12 : 16,
      },
      grid: {
        display: "grid",
        gridTemplateColumns: isNarrow
          ? "1fr"
          : "300px minmax(620px, 1fr) 300px", // ✅ middle column gets real space
        gap: 16,
        alignItems: "start",
      },

      /* ---------- cards ---------- */
      card: {
        background: "#fff",
        border: cardBorder,
        borderRadius: 14,
        boxShadow: shadow,
        overflow: "hidden",
      },
      cardHead: {
        padding: "12px 14px",
        borderBottom: "1px solid #e6eef8",
        fontWeight: 950,
        color: "#233042",
        background: "linear-gradient(180deg, #fbfdff 0%, #f6f9fe 100%)",
      },

      /* ---------- left profile card ---------- */
      leftTop: {
        display: "grid",
        gridTemplateColumns: "86px 1fr",
        gap: 12,
        alignItems: "center",
        padding: 14,
      },
      avatarRing: {
        width: 86,
        height: 86,
        borderRadius: 999,
        padding: 4,
        background:
          "linear-gradient(135deg, rgba(59,130,246,0.55), rgba(99,102,241,0.35))",
      },
      avatar: {
        width: "100%",
        height: "100%",
        borderRadius: 999,
        objectFit: "cover",
        background: "#eef2f7",
      },
      avatarFallback2: {
        width: "100%",
        height: "100%",
        borderRadius: 999,
        display: "grid",
        placeItems: "center",
        fontWeight: 950,
        color: "#203047",
        background: "#eef2f7",
      },
      nameBig: { fontSize: 22, fontWeight: 950, lineHeight: 1.1, color: "#1f2a37" },
      subMuted: { marginTop: 4, color: "#6b7280", fontWeight: 750 },
      onlineRow: { marginTop: 8, display: "flex", alignItems: "center", gap: 8 },
      onlineDot: {
        width: 10,
        height: 10,
        borderRadius: 99,
        background: "#22c55e",
        boxShadow: "0 10px 16px rgba(34,197,94,0.25)",
      },
      onlineText: { fontSize: 12, fontWeight: 850, color: "#4b5563" },

      actionRow: {
        padding: 14,
        paddingTop: 0,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      },
      btn: {
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #d6e3f3",
        background: "#f8fbff",
        color: "#1f2a37",
        fontWeight: 900,
        cursor: "pointer",
      },
      btnPrimary: {
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid rgba(59,130,246,0.35)",
        background:
          "linear-gradient(180deg, rgba(59,130,246,0.18), rgba(59,130,246,0.10))",
        color: "#134a8a",
        fontWeight: 950,
        cursor: "pointer",
      },

      kv: { display: "grid", gridTemplateColumns: "26px 1fr", gap: 10, padding: "10px 14px" },
      kvIcon: { opacity: 0.55, fontWeight: 950 },
      kvText: { fontWeight: 850, color: "#374151" },
      kvDivider: { height: 1, background: "#e6eef8", margin: "0 14px" },

      /* ---------- middle content ---------- */
      midHeader: {
        padding: 14,
        borderBottom: "1px solid #e6eef8",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        flexWrap: "wrap",
      },
      midTitle: { fontSize: 26, fontWeight: 980, letterSpacing: -0.2, color: "#1f2a37" },
      midSubtitle: { marginTop: 3, color: "#6b7280", fontWeight: 750 },

      section: { padding: 14 },
      sectionTitle: { fontWeight: 950, color: "#243244", marginBottom: 10 },

      // ✅ FIX: About box looks tiny on desktop
      aboutBox: {
        border: "1px solid #e6eef8",
        background: "#fbfdff",
        borderRadius: 12,
        padding: isNarrow ? 12 : 16,
        color: "#374151",
        lineHeight: 1.7,
        fontWeight: 750,
        fontSize: isNarrow ? 14 : 15,
        minHeight: isNarrow ? 90 : 140, // ✅ taller on web
        whiteSpace: "pre-wrap", // ✅ keep line breaks
        wordBreak: "break-word",
      },

      chips: { display: "flex", flexWrap: "wrap", gap: 10 },
      chip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 12px",
        borderRadius: 999,
        border: "1px solid #dbe4ef",
        background: "#f7fbff",
        fontWeight: 900,
        color: "#2b3a55",
      },
      chipIcon: {
        width: 18,
        height: 18,
        borderRadius: 6,
        background: "rgba(47,93,150,0.12)",
        display: "grid",
        placeItems: "center",
        fontSize: 12,
      },

      infoGrid: {
        display: "grid",
        gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
        gap: 12,
      },
      infoRow: {
        border: "1px solid #e6eef8",
        borderRadius: 12,
        background: "#fff",
        padding: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
      },
      infoLabel: { color: "#6b7280", fontWeight: 850 },
      infoValue: { color: "#1f2a37", fontWeight: 950 },

      /* ---------- right column ---------- */
      galleryHeadRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
      },
      pager: { display: "flex", alignItems: "center", gap: 8, color: "#6b7280", fontWeight: 850 },
      pagerBtn: {
        border: "1px solid #dbe4ef",
        background: "#f8fbff",
        borderRadius: 10,
        padding: "6px 10px",
        cursor: "pointer",
        fontWeight: 900,
      },

      thumbs: { padding: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
      thumb: {
        borderRadius: 12,
        border: "1px solid #dbe4ef",
        overflow: "hidden",
        background: "#f3f6fb",
        aspectRatio: "4 / 3",
      },
      thumbImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },

      /* ---------- empty/loading ---------- */
      center: {
        minHeight: "70vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        padding: 16,
      },
      hint: {
        maxWidth: 520,
        textAlign: "center",
        color: "#6b7280",
        fontWeight: 750,
        lineHeight: 1.5,
      },
    };
  }, [isNarrow]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u?.uid) {
        nav("/login", { replace: true });
        return;
      }

      setLoading(true);
      try {
        const snap = await getDoc(doc(db, "datingProfiles", u.uid));
        if (!snap.exists()) {
          setProfile(null);
          setLoading(false);
          return;
        }

        const p = snap.data() || {};
        const computedAge = calcAgeFromBirthDateAny(p.birthDate);
        const ageFinal = Number.isFinite(Number(p.age)) ? Number(p.age) : computedAge;

        const normalized = {
          uid: u.uid,
          email: safe(p.email || u.email),

          displayName: safe(p.displayName || u.displayName),
          username: safe(p.username),

          gender: safe(p.gender),
          lookingFor: safe(p.lookingFor),

          birthDate: p.birthDate,
          age: Number.isFinite(Number(ageFinal)) ? Number(ageFinal) : null,

          city: safe(p.city),
          region: safe(p.region),
          country: safe(p.country) || "Ghana",

          bio: safe(p.bio),

          interests: uniq(p.interests),
          languages: uniq(p.languages),

          photos: uniq(p.photos),
          avatar: safe(p.avatar),

          height: p.height != null && p.height !== "" ? String(p.height) : "",
          education: safe(p.education),
          job: safe(p.job),
          jobOther: safe(p.jobOther),

          relationshipGoal: safe(p.relationshipGoal),
          smoking: safe(p.smoking),
          drinking: safe(p.drinking),
          religion: safe(p.religion),

          verified: Boolean(p.verified),
          profileComplete: Boolean(p.profileComplete),
        };

        setProfile(normalized);
      } catch (e) {
        console.error("ProfileDetails load error:", e);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [nav]);

  const gallery = useMemo(() => {
    const hero = safe(profile?.avatar);
    const rest = Array.isArray(profile?.photos) ? profile.photos : [];
    return uniq([hero, ...rest]).filter(Boolean);
  }, [profile]);

  const titleName = profile?.displayName || "Your Profile";
  const ageText = profile?.age != null ? `, ${profile.age}` : "";
  const locationLine = [safe(profile?.city), safe(profile?.region), safe(profile?.country)]
    .filter(Boolean)
    .join(", ");

  // Right-side gallery paging
  const [pageIdx, setPageIdx] = useState(0);
  useEffect(() => setPageIdx(0), [profile?.uid]);

  const thumbsPerPage = 4;
  const totalPages = Math.max(1, Math.ceil((gallery?.length || 0) / thumbsPerPage));
  const pageSafe = Math.min(pageIdx, totalPages - 1);
  const start = pageSafe * thumbsPerPage;
  const currentThumbs = (gallery || []).slice(start, start + thumbsPerPage);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.topBar}>
          <div style={styles.topBarInner}>
            <div style={styles.brand} onClick={() => nav("/profile")} role="button" tabIndex={0}>
              <span style={styles.brandDot} />
              <span>GhanaDating</span>
            </div>

            <div style={styles.rightTop}>
              <button style={styles.topBackBtn} type="button" onClick={() => nav("/profile")}>
                ← Back
              </button>
              <div style={styles.topAvatarFallback}>{initials("U")}</div>
            </div>
          </div>
        </div>

        <div style={styles.container}>
          <div style={styles.center}>
            <div style={{ fontWeight: 950, fontSize: 16 }}>Loading profile…</div>
            <div style={styles.hint}>Fetching your saved details.</div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={styles.page}>
        <div style={styles.topBar}>
          <div style={styles.topBarInner}>
            <div style={styles.brand} onClick={() => nav("/profile")} role="button" tabIndex={0}>
              <span style={styles.brandDot} />
              <span>GhanaDating</span>
            </div>

            <div style={styles.rightTop}>
              <button style={styles.topBackBtn} type="button" onClick={() => nav("/profile")}>
                ← Back
              </button>
              <div style={styles.topAvatarFallback}>{initials("U")}</div>
            </div>
          </div>
        </div>

        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.cardHead}>Profile</div>
            <div style={styles.center}>
              <div style={{ fontWeight: 950, fontSize: 16 }}>No profile found.</div>
              <div style={styles.hint}>Create your profile so people can discover you.</div>
              <button style={styles.btnPrimary} onClick={() => nav("/edit-profile")} type="button">
                Create Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const heroPhoto = gallery?.[0] || "";
  const topAvatarNode = heroPhoto ? (
    <img src={heroPhoto} alt="me" style={styles.topAvatar} />
  ) : (
    <div style={styles.topAvatarFallback}>{initials(titleName)}</div>
  );

  return (
    <div style={styles.page}>
      {/* TOP NAV (clean) */}
      <div style={styles.topBar}>
        <div style={styles.topBarInner}>
          <div style={styles.brand} onClick={() => nav("/profile")} role="button" tabIndex={0}>
            <span style={styles.brandDot} />
            <span>GhanaDating</span>
          </div>

          <div style={styles.rightTop}>
            <button style={styles.topBackBtn} type="button" onClick={() => nav("/profile")}>
              ← Back
            </button>
            {topAvatarNode}
          </div>
        </div>
      </div>

      {/* BODY */}
      <div style={styles.container}>
        <div style={styles.grid}>
          {/* LEFT */}
          <div style={styles.card}>
            <div style={styles.leftTop}>
              <div style={styles.avatarRing}>
                {heroPhoto ? (
                  <img src={heroPhoto} alt="avatar" style={styles.avatar} />
                ) : (
                  <div style={styles.avatarFallback2}>{initials(titleName)}</div>
                )}
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={styles.nameBig}>
                  {titleName}
                  <span style={{ fontWeight: 850, color: "#6b7280" }}>{ageText}</span>
                </div>
                <div style={styles.subMuted}>{locationLine || "—"}</div>

                <div style={styles.onlineRow}>
                  <span style={styles.onlineDot} />
                  <span style={styles.onlineText}>Online now</span>
                </div>
              </div>
            </div>

            <div style={styles.actionRow}>
              {/* ✅ ONLY ONE Edit button */}
              <button style={styles.btnPrimary} type="button" onClick={() => nav("/edit-profile")}>
                ✏️ Edit Profile
              </button>

              {/* ✅ Back to profile screen */}
              <button style={styles.btn} type="button" onClick={() => nav("/profile")}>
                ← Back to Profile
              </button>
            </div>

            <div style={styles.kvDivider} />

            <div style={styles.cardHead}>Basic Info</div>

            <div style={styles.kv}>
              <div style={styles.kvIcon}>👤</div>
              <div style={styles.kvText}>{valueOrDash(prettyEnum(profile.gender))}</div>
            </div>
            <div style={styles.kvDivider} />
            <div style={styles.kv}>
              <div style={styles.kvIcon}>🎂</div>
              <div style={styles.kvText}>{profile.age != null ? `${profile.age}` : "—"}</div>
            </div>
            <div style={styles.kvDivider} />
            <div style={styles.kv}>
              <div style={styles.kvIcon}>📍</div>
              <div style={styles.kvText}>
                {valueOrDash(profile.city || profile.region || profile.country)}
              </div>
            </div>
            <div style={styles.kvDivider} />
            <div style={styles.kv}>
              <div style={styles.kvIcon}>💘</div>
              <div style={styles.kvText}>
                {profile.relationshipGoal ? prettyEnum(profile.relationshipGoal) : "—"}
              </div>
            </div>
          </div>

          {/* MIDDLE */}
          <div style={styles.card}>
            <div style={styles.midHeader}>
              <div>
                <div style={styles.midTitle}>
                  {titleName}
                  <span style={{ fontWeight: 850, color: "#6b7280" }}>{ageText}</span>
                </div>
                <div style={styles.midSubtitle}>{locationLine || "—"}</div>
              </div>
            </div>

            <div style={styles.section}>
              <div style={styles.sectionTitle}>About Me</div>
              <div style={styles.aboutBox}>
                {profile.bio ? profile.bio : "No bio yet. Add one to get better matches."}
              </div>
            </div>

            <div style={styles.section}>
              <div style={styles.sectionTitle}>Interests</div>
              <div style={styles.chips}>
                {(profile.interests || []).length ? (
                  profile.interests.slice(0, 18).map((t, i) => (
                    <span key={String(t) + i} style={styles.chip}>
                      <span style={styles.chipIcon}>★</span>
                      {titleCase(t)}
                    </span>
                  ))
                ) : (
                  <span style={{ color: "#6b7280", fontWeight: 800 }}>No interests yet.</span>
                )}
              </div>
            </div>

            <div style={styles.section}>
              <div style={styles.sectionTitle}>Basic Info</div>
              <div style={styles.infoGrid}>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Looking for</div>
                  <div style={styles.infoValue}>{valueOrDash(prettyEnum(profile.lookingFor))}</div>
                </div>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Education</div>
                  <div style={styles.infoValue}>
                    {profile.education ? prettyEnum(profile.education) : "—"}
                  </div>
                </div>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Job</div>
                  <div style={styles.infoValue}>{displayJob(profile) || "—"}</div>
                </div>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Height</div>
                  <div style={styles.infoValue}>{profile.height ? `${profile.height} cm` : "—"}</div>
                </div>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Drinking</div>
                  <div style={styles.infoValue}>
                    {profile.drinking ? prettyEnum(profile.drinking) : "—"}
                  </div>
                </div>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Smoking</div>
                  <div style={styles.infoValue}>
                    {profile.smoking ? prettyEnum(profile.smoking) : "—"}
                  </div>
                </div>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Religion</div>
                  <div style={styles.infoValue}>
                    {profile.religion ? prettyEnum(profile.religion) : "—"}
                  </div>
                </div>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Languages</div>
                  <div style={styles.infoValue}>
                    {(profile.languages || []).length
                      ? profile.languages.slice(0, 3).map(titleCase).join(", ")
                      : "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div style={{ display: "grid", gap: 16 }}>
            <div style={styles.card}>
              <div style={styles.cardHead}>
                <div style={styles.galleryHeadRow}>
                  <span>Photo Gallery</span>
                  <span style={styles.pager}>
                    <button
                      type="button"
                      style={styles.pagerBtn}
                      onClick={() => setPageIdx((x) => Math.max(0, x - 1))}
                      disabled={pageSafe <= 0}
                      title="Previous"
                    >
                      ‹
                    </button>
                    <span>
                      {gallery?.length
                        ? `${Math.min(start + thumbsPerPage, gallery.length)} of ${gallery.length}`
                        : "0 of 0"}
                    </span>
                    <button
                      type="button"
                      style={styles.pagerBtn}
                      onClick={() => setPageIdx((x) => Math.min(totalPages - 1, x + 1))}
                      disabled={pageSafe >= totalPages - 1}
                      title="Next"
                    >
                      ›
                    </button>
                  </span>
                </div>
              </div>

              <div style={styles.thumbs}>
                {currentThumbs.length ? (
                  currentThumbs.map((url, idx) => (
                    <div key={url + idx} style={styles.thumb} title="Photo">
                      <img src={url} alt={`photo-${idx}`} style={styles.thumbImg} />
                    </div>
                  ))
                ) : (
                  <div style={{ gridColumn: "1 / -1", color: "#6b7280", fontWeight: 850 }}>
                    No photos yet.
                  </div>
                )}
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardHead}>Quick</div>
              <div style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button style={styles.btn} type="button" onClick={() => nav("/profile")}>
                  ← Back to Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}